import React, { useCallback, useMemo } from 'react';
import type {
  FeaturedCreatorProductType,
  TextPlaceholderLine,
} from '@/shared/types/marketplace.interface';
import classNames from 'classnames';
import Image from 'next/image';
import { zeroPaddedNumber } from '@/shared/utils/utils';
import PopularProduct from '@/components/Shop/PopularProduct';
import { useConfigProvider } from '@/lib/context/ConfigProvider';
import { Carousel } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { ProductPlaceholder } from '@/components/Common';
import './index.less';

const PopularProducts = ({
  products,
  vertical,
  numbered,
}: {
  products: FeaturedCreatorProductType[];
  vertical: boolean;
  numbered: boolean;
}) => {
  const { isMobile, screenSize } = useConfigProvider();
  const SamplePrevArrow = ({ slideCount, currentSlide, ...props }: any) => (
    <div {...props} data-slide-count={slideCount} data-current-slide={currentSlide}>
      <LeftOutlined />
    </div>
  );

  const SampleNextArrow = ({ slideCount, currentSlide, ...props }: any) => (
    <div {...props} data-slide-count={slideCount} data-current-slide={currentSlide}>
      <RightOutlined />
    </div>
  );

  const getSlidesToShow = useMemo((): number => {
    // [320, 2],
    //   [768, 3],
    //   [1024, 5],
    //   [1280, 6],
    if (screenSize?.width > 1280) {
      return 6;
    }
    if (screenSize?.width > 1024 && screenSize?.width <= 1280) {
      return 5;
    }
    if (screenSize?.width > 768 && screenSize?.width <= 1024) {
      return 3;
    }
    return 2;
  }, [screenSize?.width]);

  const productInfoLines = useMemo((): TextPlaceholderLine[] => {
    return [
      { width: '100%', height: '30px', color: '#e8e8e8' },
      { width: '90%', height: '10px', color: '#e8e8e8' },
      { width: '50%', height: '10px', color: '#e8e8e8' },
    ];
  }, []);

  const sliderSettings = useMemo(
    () => ({
      slidesToShow: getSlidesToShow,
      dots: false,
      infinite: true,
      speed: 500,
      slidesToScroll: 1,
      draggable: false,
      arrows: !isMobile,
      autoPlay: true,
      classNames: 'carousel',
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
    }),
    [getSlidesToShow, isMobile],
  );

  return (
    <div
      className={classNames('popular-products-container', {
        vertical: vertical,
      })}
    >
      <div className="title-container">
        <span className="title">Popular Products </span>
        <Image
          className="star"
          src="/images/star.png"
          alt="star"
          width={22}
          height={22}
          title="star"
        />
      </div>
      {vertical && !isMobile ? (
        <div className="popular-products">
          {products.length > 0 ? (
            <>
              {products.map((product, i) => (
                <div className="product-item" key={`product-${i}`}>
                  {numbered && <span className="product-number">{zeroPaddedNumber(i + 1)}</span>}
                  <PopularProduct product={product} />
                </div>
              ))}
            </>
          ) : (
            new Array(getSlidesToShow)
              .fill(1)
              .map((_, index) => (
                <ProductPlaceholder
                  key={`product-placeholder-${index}`}
                  textLines={productInfoLines}
                />
              ))
          )}
        </div>
      ) : (
        <div className="popular-products">
          <Carousel {...sliderSettings}>
            {products.length > 0
              ? products.map((product, i) => (
                  <div className="carousel-slide" key={`carousel-item-${i}`}>
                    <PopularProduct product={product} />
                  </div>
                ))
              : new Array(getSlidesToShow).fill(1).map((_, index) => (
                  <div className="carousel-slide" key={`carousel-item-${index}`}>
                    <ProductPlaceholder textLines={productInfoLines} />
                  </div>
                ))}
          </Carousel>
        </div>
      )}
    </div>
  );
};

export default PopularProducts;
